import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faExclamationCircle,
  faUserGraduate,
  faTimes,
  faCheckCircle,
  faPlus,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import EditStudentModal from '../../components/admin/EditStudentModal';

interface StudentAdmin {
  userId: string;
  studentId: string;
  fullName: string;
  email: string;
  status: number;
  enrolledLearnerships: string[];
}

interface LearnershipOption {
  id: string;
  title: string;
  isActive: boolean;
}

const statusMap: { [key: number]: { label: string; className: string } } = {
  0: { label: 'Active', className: 'bg-green-100 text-green-700' },
  1: { label: 'Inactive', className: 'bg-gray-100 text-gray-700' },
  2: { label: 'Suspended', className: 'bg-red-100 text-red-700' },
  3: { label: 'Graduated', className: 'bg-blue-100 text-blue-700' },
};

export default function AdminStudentsPage() {
  const [students, setStudents] = useState<StudentAdmin[]>([]);
  const [learnerships, setLearnerships] = useState<LearnershipOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editUserId, setEditUserId] = useState<string | null>(null);

  // Enroll modal state
  const [enrollModalStudent, setEnrollModalStudent] = useState<StudentAdmin | null>(null);
  const [selectedLearnershipId, setSelectedLearnershipId] = useState('');
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState('');
  const [enrollSuccess, setEnrollSuccess] = useState(false);

  const { accessToken } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const headers = { Authorization: `Bearer ${accessToken}` };
      const [studentsRes, learnershipsRes] = await Promise.all([
        fetch('/api/admin/students', { headers }),
        fetch('/api/admin/learnerships', { headers }),
      ]);

      if (!studentsRes.ok || !learnershipsRes.ok) throw new Error('Failed to load data');

      setStudents(await studentsRes.json());
      setLearnerships(await learnershipsRes.json());
    } catch (err) {
      console.error(err);
      setError('Failed to load students. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const openEnrollModal = (student: StudentAdmin) => {
    setEnrollModalStudent(student);
    setSelectedLearnershipId('');
    setEnrollError('');
    setEnrollSuccess(false);
  };

  const handleEnroll = async () => {
    if (!enrollModalStudent || !selectedLearnershipId) return;

    try {
      setIsEnrolling(true);
      setEnrollError('');

      const response = await fetch('/api/admin/enrollments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          studentUserId: enrollModalStudent.userId,
          learnershipId: selectedLearnershipId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setEnrollError(data.message || 'Failed to enroll student.');
        return;
      }

      setEnrollSuccess(true);
      await fetchData();
      setTimeout(() => {
        setEnrollModalStudent(null);
      }, 1200);
    } catch (err) {
      console.error(err);
      setEnrollError('An error occurred while enrolling the student.');
    } finally {
      setIsEnrolling(false);
    }
  };

  const filteredStudents = students.filter(
    (s) =>
      s.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.studentId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Learnerships the selected student isn't already enrolled in
  const availableLearnerships = learnerships.filter(
    (l) => l.isActive && !enrollModalStudent?.enrolledLearnerships.includes(l.title)
  );

  return (
    <>
      {/* Top Bar */}
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">Students</h1>
        <p className="text-sm text-brand-900/60 mt-1">
          Manage students and assign them to courses
        </p>
      </div>

      <div className="p-8">
        {!isLoading && !error && (
          <div className="mb-6">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, student ID, or email..."
              className="w-full max-w-md px-4 py-2.5 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoading && !error && filteredStudents.length === 0 && (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faUserGraduate} className="h-12 w-12 text-brand-300 mb-4" />
            <h3 className="text-lg font-bold text-brand-950">No students found</h3>
            <p className="text-sm text-brand-900/60 mt-2">
              {students.length === 0
                ? 'Register a student account to get started.'
                : 'Try a different search term.'}
            </p>
          </div>
        )}

        {!isLoading && !error && filteredStudents.length > 0 && (
          <div className="card-surface overflow-hidden">
            <div className="hidden md:grid grid-cols-[2fr_1.5fr_2fr_1fr_auto] gap-4 px-5 py-3 bg-canvas-50 text-xs font-bold uppercase tracking-wider text-brand-900/50">
              <span>Student</span>
              <span>Student ID</span>
              <span>Enrolled Courses</span>
              <span>Status</span>
              <span></span>
            </div>
            <div className="divide-y divide-canvas-200">
              {filteredStudents.map((student) => {
                const status = statusMap[student.status] ?? statusMap[0];
                return (
                  <div
                    key={student.userId}
                    className="grid grid-cols-1 md:grid-cols-[2fr_1.5fr_2fr_1fr_auto] gap-2 md:gap-4 px-5 py-4 items-center"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-brand-950 truncate">
                        {student.fullName}
                      </p>
                      <p className="text-xs text-brand-900/50 truncate">{student.email}</p>
                    </div>
                    <p className="text-sm text-brand-900/70">{student.studentId}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {student.enrolledLearnerships.length === 0 ? (
                        <span className="text-xs text-brand-900/40">No courses yet</span>
                      ) : (
                        student.enrolledLearnerships.map((title) => (
                          <span
                            key={title}
                            className="px-2 py-0.5 bg-brand-50 text-brand-700 text-xs font-medium rounded-full"
                          >
                            {title}
                          </span>
                        ))
                      )}
                    </div>
                    <span
                      className={`inline-block w-fit px-2.5 py-1 rounded-full text-xs font-semibold ${status.className}`}
                    >
                      {status.label}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditUserId(student.userId)}
                        className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors flex items-center gap-1.5 w-fit"
                      >
                        <FontAwesomeIcon icon={faPen} className="h-3 w-3" />
                        Edit
                      </button>
                      <button
                        onClick={() => openEnrollModal(student)}
                        className="px-3 py-1.5 bg-brand-gradient text-white text-xs font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-1.5 w-fit"
                      >
                        <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
                        Enroll
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {editUserId && <EditStudentModal userId={editUserId} onClose={() => setEditUserId(null)} onSaved={fetchData} />}

      {/* Enroll Modal */}
      {enrollModalStudent && (
        <div className="fixed inset-0 bg-brand-950/40 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-brand-950">Enroll in Course</h3>
              <button
                onClick={() => setEnrollModalStudent(null)}
                className="p-1.5 hover:bg-canvas-100 rounded-lg text-brand-900/60"
              >
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <p className="text-sm text-brand-900/60 mb-4">
              Enrolling <span className="font-semibold text-brand-950">{enrollModalStudent.fullName}</span>
            </p>

            {enrollSuccess ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <FontAwesomeIcon icon={faCheckCircle} className="h-5 w-5 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-700 font-medium">Student enrolled successfully!</p>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Select course</label>
                  <select
                    value={selectedLearnershipId}
                    onChange={(e) => setSelectedLearnershipId(e.target.value)}
                    className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="">Choose a course...</option>
                    {availableLearnerships.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.title}
                      </option>
                    ))}
                  </select>
                  {availableLearnerships.length === 0 && (
                    <p className="text-xs text-brand-900/50 mt-2">
                      This student is already enrolled in all available active courses.
                    </p>
                  )}
                </div>

                {enrollError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                    <FontAwesomeIcon icon={faExclamationCircle} className="h-4 w-4 text-red-700 flex-shrink-0" />
                    <p className="text-sm text-red-700">{enrollError}</p>
                  </div>
                )}

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setEnrollModalStudent(null)}
                    className="px-5 py-2 border border-canvas-300 text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEnroll}
                    disabled={isEnrolling || !selectedLearnershipId}
                    className="px-5 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
                  >
                    <FontAwesomeIcon
                      icon={isEnrolling ? faSpinner : faCheckCircle}
                      className={`h-3.5 w-3.5 ${isEnrolling ? 'animate-spin' : ''}`}
                    />
                    {isEnrolling ? 'Enrolling...' : 'Confirm Enrollment'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
